import { useDatoClient, useDatoPerson } from '@src/stores-v2/StoreContext';
import { useVideoCreatorStore } from '@src/stores-v2/VideoCreatorStoreContext';
import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

/**
 * Resolve the active organization from the URL when none is loaded yet (e.g. a
 * hard refresh or deep-link into an org-scoped page).
 *
 * Handles both deep-link forms, `storyId` taking precedence — matching
 * `useVideoCreatorDataInitialization` — but stays lightweight: a `storyId`
 * resolves via the story's referencing showcases, a `showcase` slug via a
 * single showcase lookup. Neither triggers the heavy `initializeData`
 * story/video/transcript load. No-op once an org is present or when neither
 * param is in the URL.
 *
 * @returns `resolving` — true while a URL param is being resolved into an org,
 * so callers can show a loading state instead of an empty/"no org" message.
 */
export function useResolveOrgFromUrlParams(): boolean {
  const datoClientStore = useDatoClient();
  const datoPerson = useDatoPerson();
  const videoCreator = useVideoCreatorStore();

  const album = datoPerson.selectedOrganization || videoCreator.organization;
  const [urlSearchParams] = useSearchParams();
  const storyIdParam = urlSearchParams.get('storyId');
  const showcaseParam = urlSearchParams.get('showcase');

  // Start resolving (no flash) when an org isn't loaded but a param can supply
  // one. Lazy init so a deep-link's first paint already shows the loading state.
  const [resolving, setResolving] = useState(
    () => !album && !!(storyIdParam || showcaseParam),
  );

  useEffect(() => {
    if (album || (!storyIdParam && !showcaseParam)) {
      setResolving(false);
      return;
    }
    const repo = datoClientStore.albumRepository;
    if (!repo) return;

    let cancelled = false;
    setResolving(true);
    (async () => {
      try {
        if (storyIdParam) {
          const showcases = await repo.findAllWithStoryAccess(storyIdParam);
          if (cancelled || !showcases.length) return;
          const showcase = showcases[0];
          videoCreator.organization = {
            ...showcase,
            title: showcase.title || showcase.organization,
            stories: [],
            logo: { url: '' },
            mainImage: { url: '' },
            contributors: [],
          };
        } else if (showcaseParam) {
          // Only the org is needed here; populating every story's video
          // would fire one slow request per 50 stories.
          const showcase = await repo.findOneBySlug(showcaseParam, {
            populateStoryVideos: false,
          });
          if (cancelled || !showcase) return;
          videoCreator.organization = showcase;
        }
      } finally {
        if (!cancelled) setResolving(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [album, storyIdParam, showcaseParam, datoClientStore, videoCreator]);

  return resolving;
}
